import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import { Upload, FileText, Key, ShieldCheck, X, Loader2, Download, AlertTriangle } from "lucide-react";

const BACKEND_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/+$/, "");
const API = `${BACKEND_BASE}/api`;
const AGENT_URL = "http://localhost:7979";

export default function PopupPage() {
  const [step, setStep] = useState(0); // 0=upload, 1=sign, 2=done
  const [file, setFile] = useState(null);
  const [docId, setDocId] = useState(null);
  const [hashToSign, setHashToSign] = useState(null);
  const [signType, setSignType] = useState("auto");
  const [pin, setPin] = useState("");
  const [signing, setSigning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [agentOnline, setAgentOnline] = useState(null);
  const [isBirdId, setIsBirdId] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [selectedCertPem, setSelectedCertPem] = useState(null);
  const fileRef = useRef();

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setError(null);
    let certPemForPrepare = null;

    // Check agent
    try {
      const statusRes = await fetch(`${AGENT_URL}/status`, { signal: AbortSignal.timeout(2000) });
      const statusData = await statusRes.json();
      setAgentOnline(true);
      setIsBirdId(!!statusData.birdid_detected);
      try {
        const certsRes = await fetch(`${AGENT_URL}/certificates`, { signal: AbortSignal.timeout(3000) });
        const certsData = await certsRes.json();
        const certs = certsData.certificates || [];
        if (certs.length > 0) {
          setSelectedCert(certs[0].alias);
          setSelectedCertPem(certs[0].cert_pem || null);
          certPemForPrepare = certs[0].cert_pem || null;
        }
        if (certs.some(c => c.is_birdid)) setIsBirdId(true);
      } catch {
        // keep selectedCert/selectedCertPem null
      }
    } catch {
      setAgentOnline(false);
    }

    // Upload
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("sign_type", signType);
      const res = await axios.post(`${API}/documents/upload`, fd);
      setDocId(res.data.document_id);
      const preparePayload = certPemForPrepare ? { certificate_pem: certPemForPrepare } : undefined;
      const prep = await axios.post(`${API}/sign/prepare/${res.data.document_id}`, preparePayload);
      setHashToSign(prep.data.hash);
      setStep(1);
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data?.error || e.message;
      const status = e.response?.status ? ` [${e.response.status}]` : "";
      setError(`Erro ao processar arquivo${status}: ${detail}`);
    }
  };

  const handleSign = async () => {
    if (!docId || !hashToSign) return;
    setSigning(true);
    setError(null);
    try {
      if (!agentOnline) {
        const res = await axios.post(`${API}/sign/demo/${docId}`);
        setResult(res.data);
        setStep(2);
        window.opener?.postMessage({ type: "SIGNATURE_COMPLETE", result: res.data }, "*");
        return;
      }
      let agentData;
      try {
        const agentRes = await axios.post(`${AGENT_URL}/sign`, { hash: hashToSign, pin, certificate_alias: selectedCert, mode: isBirdId ? "birdid" : "a3token" }, { timeout: 20000 });
        agentData = agentRes.data;
      } catch (err) {
        if (err.response && err.response.data) {
          agentData = err.response.data;
        } else {
          throw err;
        }
      }
      if (agentData.error) throw new Error(agentData.error);
      const completeRes = await axios.post(`${API}/sign/complete`, {
        document_id: docId,
        signature_base64: agentData.signature,
        certificate_pem: agentData.certificate_pem,
      });
      setResult(completeRes.data);
      setStep(2);
      window.opener?.postMessage({ type: "SIGNATURE_COMPLETE", result: completeRes.data }, "*");
    } catch (e) {
      setError(e.message);
      window.opener?.postMessage({ type: "SIGNATURE_ERROR", error: e.message }, "*");
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/download/signed`);
      const { filename, content_base64 } = res.data;
      const bytes = atob(content_base64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      const blob = new Blob([arr]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-white/90 backdrop-blur-xl flex items-center justify-center p-4"
         style={{ background: "linear-gradient(135deg, rgba(22,58,36,0.03) 0%, rgba(143,188,143,0.05) 100%)" }}>
      <div className="w-full max-w-sm bg-white rounded-2xl border border-black/10 shadow-2xl overflow-hidden" data-testid="popup-widget">
        {/* Header */}
        <div className="bg-[#163A24] p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#8FBC8F]" />
            <span className="text-white font-semibold text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Assinador Digital</span>
          </div>
          <button onClick={() => window.close()} className="text-white/50 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          {/* Step 0: Upload */}
          {step === 0 && (
            <div className="space-y-4" data-testid="popup-step-upload">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Selecionar documento</p>
                <p className="text-xs text-gray-400">PDF, XML ou outro arquivo para assinar</p>
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-8 flex flex-col items-center cursor-pointer hover:border-[#163A24]/40 hover:bg-gray-50 transition-all"
              >
                <input ref={fileRef} type="file" className="hidden" onChange={(e) => handleFile(e.target.files[0])} data-testid="popup-file-input" />
                <Upload size={24} className="text-gray-300 mb-2" />
                <p className="text-xs text-gray-500">Clique para selecionar</p>
              </div>
              <select
                value={signType}
                onChange={(e) => setSignType(e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#163A24]"
                data-testid="popup-sign-type"
              >
                <option value="auto">Automático</option>
                <option value="PAdES">PAdES (PDF)</option>
                <option value="XAdES">XAdES (XML)</option>
                <option value="CAdES">CAdES (Genérico)</option>
              </select>
            </div>
          )}

          {/* Step 1: Sign */}
          {step === 1 && (
            <div className="space-y-4" data-testid="popup-step-sign">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-[#163A24]" />
                <p className="text-sm font-medium text-gray-700">{file?.name}</p>
              </div>
              {agentOnline === false && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg text-xs text-yellow-700 border border-yellow-200">
                  <AlertTriangle size={12} className="mt-0.5" />
                  <span>Agente não encontrado. Assinando com certificado de demonstração.</span>
                </div>
              )}
              {agentOnline && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{isBirdId ? "Código OTP do BirdID (6 dígitos)" : "PIN do certificado A3"}</label>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="PIN"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#163A24]"
                    data-testid="popup-pin"
                  />
                </div>
              )}
              {error && <p className="text-xs text-red-600" data-testid="popup-error">{error}</p>}
              <button
                data-testid="popup-btn-sign"
                onClick={handleSign}
                disabled={signing}
                className="w-full flex items-center justify-center gap-2 bg-[#163A24] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#205234] disabled:opacity-50 transition-colors"
              >
                {signing ? <><Loader2 size={14} className="animate-spin" />Assinando...</> : <><Key size={14} />Assinar</>}
              </button>
              <button onClick={() => setStep(0)} className="w-full text-xs text-gray-400 hover:text-gray-600">Voltar</button>
            </div>
          )}

          {/* Step 2: Done */}
          {step === 2 && result && (
            <div className="space-y-4 text-center" data-testid="popup-step-done">
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <ShieldCheck size={24} className="text-green-600" />
              </div>
              <div>
                <p className="font-bold text-gray-800 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Assinado com sucesso!</p>
                <p className="text-xs text-gray-400 mt-1">{result.sign_type} · {new Date(result.signed_at).toLocaleString("pt-BR")}</p>
              </div>
              <button
                data-testid="popup-btn-download"
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 bg-[#163A24] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#205234] transition-colors"
              >
                <Download size={14} /> Baixar Assinado
              </button>
              <button onClick={() => window.close()} className="w-full text-xs text-gray-400 hover:text-gray-600">Fechar</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
